import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';
import { validateSenderID } from './customButtonsApi';

const WebhookParamsModal = ({
  isOpen,
  onClose,
  title = 'Webhook Parameters',
  requiredParams = {},
  onSubmit,
  placeholders = []
}) => {
  const [formValues, setFormValues] = useState({});
  const [selectedInsertByParam, setSelectedInsertByParam] = useState({});
  const [addHeaders, setAddHeaders] = useState(false);
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [senderId, setSenderId] = useState('');
  const [saving, setSaving] = useState(false);
  const [senderIdError, setSenderIdError] = useState('');

  const paramKeys = useMemo(() => Object.keys(requiredParams || {}), [requiredParams]);

  useEffect(() => {
    if (!isOpen) return;
    const initialValues = {};
    paramKeys.forEach((key) => {
      initialValues[key] = requiredParams?.[key] ?? '';
    });
    setFormValues(initialValues);
    setSelectedInsertByParam({});
    setAddHeaders(false);
    setHeaderKey('');
    setHeaderValue('');
    setSenderId('');
    setSenderIdError('');
  }, [isOpen, paramKeys, requiredParams]);

  const handleInsertPlaceholder = (paramName, placeholder) => {
    if (!placeholder) return;
    setFormValues((prev) => ({
      ...prev,
      [paramName]: `${prev?.[paramName] || ''}#{${placeholder}}`
    }));
    setSelectedInsertByParam((prev) => ({ ...prev, [paramName]: '' }));
  };

  const handleSave = async () => {
    if (addHeaders && (!headerKey.trim() || !headerValue.trim() || !senderId.trim())) {
      window.alert('Please fill headerKey, headerValue and senderId before saving.');
      return;
    }

    if (senderId && !validateSenderID(senderId)) {
      setSenderIdError('Sender ID must be only numeric or only alphabetic with length 8 to 11.');
      return;
    }

    setSenderIdError('');

    if (typeof onSubmit !== 'function') {
      onClose();
      return;
    }

    try {
      setSaving(true);
      await onSubmit({
        params: formValues,
        headerKey: addHeaders ? headerKey.trim() : '',
        headerValue: addHeaders ? headerValue.trim() : '',
        senderId: addHeaders ? senderId.trim() : ''
      });
      onClose();
    } catch (error) {
      console.error('Webhook save failed:', error);
      window.alert('Failed to save webhook parameters. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </>
      )}
    >
      <div className="space-y-4">
        {!paramKeys.length ? (
          <p className="text-sm text-gray-600">All parameters were passed at webhook creation. Click save to start webhook service.</p>
        ) : (
          paramKeys.map((paramName) => (
            <div key={paramName} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{paramName}</label>
              <input
                type="text"
                value={formValues?.[paramName] || ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setFormValues((prev) => ({ ...prev, [paramName]: value }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`Enter value for ${paramName}`}
              />
              <div className="flex items-center gap-2">
                <select
                  value={selectedInsertByParam?.[paramName] || ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedInsertByParam((prev) => ({ ...prev, [paramName]: value }));
                    handleInsertPlaceholder(paramName, value);
                  }}
                  className="w-full px-2 py-2 text-sm border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">Insert placeholder</option>
                  {placeholders.map((item) => {
                    const value = item?.LeadsColums || item?.Text || item?.Code || '';
                    if (!value) return null;
                    return (
                      <option key={value} value={value}>{value}</option>
                    );
                  })}
                </select>
              </div>
            </div>
          ))
        )}

        <div className="border-t border-gray-200 pt-4 space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={addHeaders}
              onChange={(event) => setAddHeaders(event.target.checked)}
              className="w-4 h-4"
            />
            Add Header Details
          </label>

          {addHeaders && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={headerKey}
                onChange={(event) => setHeaderKey(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Header Key"
              />
              <input
                type="text"
                value={headerValue}
                onChange={(event) => setHeaderValue(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Header Value"
              />
              <div>
                <input
                  type="text"
                  value={senderId}
                  onChange={(event) => {
                    setSenderId(event.target.value);
                    if (senderIdError) setSenderIdError('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Sender ID"
                />
                {senderIdError && <p className="text-xs text-red-600 mt-1">{senderIdError}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default WebhookParamsModal;
